/**
 * The overlay store.
 *
 * React overlays are fed at tick rate and never at frame rate
 * (docs/13-architecture.md), so this is where the frame loop's measurements get
 * *decimated* before they cross into React. The render loop writes into a
 * mutable draft as often as it likes; the store notifies subscribers a few
 * times a second. Without that decimation the HUD would itself become the
 * frame-budget problem it exists to measure.
 */

import type { CampaignUi, FloorUi, FrameBudget, SiteCatalogUi, Transport, WorkerStats } from '../bridge';
import type { EnvironmentReport } from '../boot';
import type { Lod } from '../engine/floor';

export interface HudState {
  transport: Transport | null;
  /** Whether the *page* is cross-origin isolated, which is what allows SAB. */
  isolated: boolean;
  tickHz: number;
  /**
   * The budget every figure below is judged against, handed over by the sim at
   * boot (B0.6). `null` until the worker answers; nothing on this side invents
   * a threshold of its own.
   */
  budget: FrameBudget | null;

  /** Frame budget, smoothed over the last second. */
  fps: number;
  frameMs: number;
  frameMsP95: number;
  /**
   * **Main-thread JavaScript**: the whole `requestAnimationFrame` callback, of
   * which render and boundary are named parts. This is the line the roadmap's
   * "inside half the frame" is actually about, and until B0.6 the HUD reported
   * its two components without ever reporting the total.
   */
  scriptMs: number;
  scriptMsP95: number;
  renderMs: number;
  boundaryMs: number;
  /**
   * Long tasks a second, and the worst one, where the browser reports them.
   * The one instrument that can see work the frame loop does not do itself —
   * React reconciling an overlay, the GC, a worker message landing — which is
   * the half of "main thread" a rAF timer is blind to. Absent on browsers
   * without the `longtask` entry type, and `supported` says which.
   */
  longTasks: number;
  longestTaskMs: number;
  longTasksSupported: boolean;
  drawn: number;
  /** Rooms and tools drawn — the site's share of the instance count. */
  structures: number;
  /** Lots drawn — the flow's share. */
  lots: number;
  /** Bytes of WIP heat plane in the last publish: the section sized by the
   * *site* rather than by the load, and the one B0.6 should be watching. */
  heatBytes: number;
  alpha: number;
  /** The crowd LOD tiers in force, or `null` before the first frame. */
  lod: Lod | null;

  /** Boundary traffic. */
  publishes: number;
  publishHz: number;
  bytesPerPublish: number;
  bytesPerSecond: number;
  droppedCommands: number;
  /**
   * Command traffic (B0.6): commands and bytes a second arriving at the sim.
   * Player actions rather than frames, so the expected reading is ~0 with
   * spikes — which is the finding, not a null result: it is what justifies
   * commands staying JSON instead of being packed.
   */
  commandsPerSecond: number;
  commandBytesPerSecond: number;

  /** Memory, for the boundary panel. */
  wasmMemoryBytes: number;
  ringBytes: number;

  ui: FloorUi | null;
  /** The economy's store (M1.1), published beside the floor's every tick. */
  campaign: CampaignUi | null;
  worker: WorkerStats | null;
  /** The site catalog, handed over once at boot; the palette's source. */
  catalog: SiteCatalogUi | null;
  report: EnvironmentReport | null;
  error: string | null;
}

const initial: HudState = {
  transport: null,
  isolated: false,
  tickHz: 0,
  budget: null,
  fps: 0,
  frameMs: 0,
  frameMsP95: 0,
  scriptMs: 0,
  scriptMsP95: 0,
  renderMs: 0,
  boundaryMs: 0,
  longTasks: 0,
  longestTaskMs: 0,
  longTasksSupported: false,
  drawn: 0,
  structures: 0,
  lots: 0,
  heatBytes: 0,
  alpha: 0,
  lod: null,
  publishes: 0,
  publishHz: 0,
  bytesPerPublish: 0,
  bytesPerSecond: 0,
  droppedCommands: 0,
  commandsPerSecond: 0,
  commandBytesPerSecond: 0,
  wasmMemoryBytes: 0,
  ringBytes: 0,
  ui: null,
  campaign: null,
  worker: null,
  catalog: null,
  report: null,
  error: null,
};

let state: HudState = initial;
let draft: HudState = initial;
const listeners = new Set<() => void>();

/** Merge into the pending draft. Cheap; call from the frame loop freely. */
export function update(patch: Partial<HudState>): void {
  draft = { ...draft, ...patch };
}

/** Publish the draft to subscribers. Called at overlay rate, not frame rate. */
export function flush(): void {
  if (draft === state) return;
  state = draft;
  for (const listener of listeners) listener();
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function snapshot(): HudState {
  return state;
}
